import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import MemoizedVideoPlayer from "../components/MemoizedVideoPlayer";
import { useDebounceValue } from "../hooks/useDebounceValue";
import { useSeriesEpisodes } from "../hooks/useSeriesEpisodes";

const MuniDetail = React.memo(() => {
  const { seriesId } = useParams();
  const {
    episodes,
    seriesData: muni,
    loading: contentLoading,
    error: contentError,
  } = useSeriesEpisodes(seriesId);

  const [error, setError] = useState(null);

  // Add this function near the top of your component
  const convertLinksToAnchors = (text) => {
    if (!text) return "";

    // Regex to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return text.split(urlRegex).map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Memoize these functions to prevent re-renders
  const getStoredVideoState = useCallback(() => {
    try {
      const storedState = sessionStorage.getItem(`video-state-${seriesId}`);
      return storedState ? JSON.parse(storedState) : null;
    } catch (e) {
      console.error("Error retrieving stored video state:", e);
      return null;
    }
  }, [seriesId]);

  const saveVideoState = useCallback(
    (url, title, episodeId) => {
      try {
        const state = { url, title, episodeId };
        sessionStorage.setItem(
          `video-state-${seriesId}`,
          JSON.stringify(state)
        );
      } catch (e) {
        console.error("Error saving video state:", e);
      }
    },
    [seriesId]
  );

  // Get initial state only once
  const initialVideoState = React.useMemo(
    () => getStoredVideoState(),
    [getStoredVideoState]
  );

  // Use refs for values that shouldn't trigger re-renders
  const videoUrlRef = useRef(initialVideoState?.url || null);
  const [videoUrl, setVideoUrl] = useState(initialVideoState?.url || null);
  const debouncedVideoUrl = useDebounceValue(videoUrl, 500);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(
    initialVideoState?.title || null
  );
  const currentEpisodeIdRef = useRef(initialVideoState?.episodeId || null);
  const userHasSelectedVideo = useRef(!!initialVideoState);

  // Memoize handleEpisodeClick
  const handleEpisodeClick = useCallback(
    (episode) => {
      if (episode) {
        const url = `https://video.isilive.ca/play/${episode.videoUrl}?auto_hide=true`;
        videoUrlRef.current = url;
        currentEpisodeIdRef.current = episode.id;
        setVideoUrl(url);
        setCurrentlyPlaying(episode.title);
        userHasSelectedVideo.current = true;
        saveVideoState(url, episode.title, episode.id);
      }
    },
    [saveVideoState]
  );

  // Update video logic
  useEffect(() => {
    if (currentEpisodeIdRef.current && episodes.length > 0) {
      const currentEpisode = episodes.find(
        (ep) => ep.id === currentEpisodeIdRef.current
      );
      if (currentEpisode) {
        const url = `https://video.isilive.ca/play/${currentEpisode.videoUrl}?auto_hide=true`;
        if (url !== videoUrlRef.current) {
          videoUrlRef.current = url;
          setVideoUrl(url);
          setCurrentlyPlaying(currentEpisode.title);
        }
      }
    }
  }, [episodes]);

  // Combined loading state
  const isLoading = contentLoading;

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          Loading...
        </div>
      </div>
    );
  }

  // Update your error check to:
  if (error || contentError) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          Error: {error || contentError}
        </div>
      </div>
    );
  }

  // Render not found state
  if (!muni) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          No page found for title: {seriesId}
        </div>
      </div>
    );
  }

  // Render pay-per-view content
  if (muni && muni.tags && muni.tags.includes("ppv")) {
    return (
      <div className="flex items-center justify-center h-screen p-4 py-28">
        <iframe
          src={muni.embedLink}
          className="w-full h-full"
          title={muni.title}
          allowFullScreen
          frameBorder="0"
        ></iframe>
      </div>
    );
  }

  // Regular render
  return (
    <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="p-4 py-10"></div>
        {currentlyPlaying && (
          <div className="pl-4">Currently playing: {currentlyPlaying}</div>
        )}

        <div className="relative aspect-video" style={{ paddingTop: "56.25%" }}>
          {/* {console.log("Rendering video with URL:", debouncedVideoUrl)} */}
          {debouncedVideoUrl ? (
            <MemoizedVideoPlayer
              key={debouncedVideoUrl} // Add key prop to force remount when URL changes
              videoUrl={debouncedVideoUrl}
            />
          ) : (
            muni.thumbnail && (
              <img
                src={muni.thumbnail}
                alt={muni.title}
                className="absolute top-0 left-0 w-full h-full object-cover p-4"
              />
            )
          )}
        </div>

        <h2 className="text-white font-bold md:text-xl p-4 py-4">
          {muni.title}
        </h2>
        <p className="pl-4">
          Short Description: {convertLinksToAnchors(muni.shortDescription)}
        </p>
        <p className="pl-4">
          Long Description: {convertLinksToAnchors(muni.longDescription)}
        </p>
      </div>

      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="lg:py-10 px-10">
          <h3 className="font-bold text-lg md:text-xl">
            Videos ({episodes.length})
          </h3>
          <ul className="divide-y divide-gray-700 p-4">
            {episodes.length > 0 ? (
              episodes.map((episode) => {
                const isCurrentlyPlaying =
                  episode.id === currentEpisodeIdRef.current;

                return (
                  <li
                    key={`${episode.id}`}
                    className={`py-4 ${
                      isCurrentlyPlaying ? "bg-gray-900 rounded-md p-2" : ""
                    }`}
                  >
                    <button
                      className="w-full text-left"
                      onClick={() => handleEpisodeClick(episode)}
                    >
                      <div className="flex items-center gap-2">
                        {episode.isLive && (
                          <span className="bg-red-500 text-white px-2 py-1 rounded">
                            LIVE
                          </span>
                        )}
                        <p className="font-semibold">
                          {episode.title || "Untitled Episode"}
                        </p>
                      </div>
                      <p className="text-gray-400">
                        {episode.shortDescription ||
                          "No short description available"}
                      </p>
                      <p className="text-gray-400">
                        {episode.longDescription ||
                          "No long description available"}
                      </p>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="py-4 text-center text-gray-500">
                No videos available
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
});

MuniDetail.displayName = "MuniDetail";

export default MuniDetail;
